import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { Box } from '@mui/material'
import { QrCode, Close, FactCheck } from '@mui/icons-material'
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  TablePagination,
  TextField,
  Typography
} from '@material-ui/core'
import { Search as SearchIcon, Download } from '@material-ui/icons'
import Wrapper from 'components/Wrapper'
import ResourceNotFound from 'components/ResourceNotFound'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory, useRouteMatch } from 'react-router-dom'
import attendanceSlice, { fetchAttendeesAttendance, decideAttendanceRecord } from 'store/attendance'
import { toast } from 'utils/notify'
import { buildAttendanceQrValue, caseInsensitiveSort, getDatesInRange } from 'utils'
import moment from 'moment-timezone'
import { Roles } from 'constants/index'
import { downloadAttendanceReportInCSV } from 'utils/downloadCSV'
import QRCode from 'react-qr-code'
import { toPng } from 'html-to-image'
import ConfirmationModal from 'components/ConfirmationModal'

// ─── Cell style constants ────────────────────────────────────────────
const headerCell = {
  padding: '12px 10px',
  borderBottom: '2px solid #e0e0e0',
  borderRight: '1px solid #f0f0f0',
  fontWeight: 600,
  fontSize: 13,
  color: '#455a64',
  textAlign: 'center',
  whiteSpace: 'nowrap'
}
const stickyHeaderCell = {
  ...headerCell,
  position: 'sticky',
  left: 0,
  zIndex: 3,
  backgroundColor: '#f5f6fa',
  textAlign: 'left'
}
const bodyCell = {
  padding: '10px 12px',
  borderBottom: '1px solid #f0f0f0',
  borderRight: '1px solid #f5f5f5',
  fontSize: 13
}
const stickyCell = {
  ...bodyCell,
  position: 'sticky',
  left: 0,
  zIndex: 1
}
const bodyCellCenter = { ...bodyCell, textAlign: 'center', verticalAlign: 'top' }

// ─── Sub-components ──────────────────────────────────────────────────
function DayColumnHeader({ date }) {
  const m = moment(date, 'MM/DD/YYYY')
  return (
    <div style={{ lineHeight: 1.5 }}>
      <div style={{ fontSize: 13, color: '#263238' }}>
        <span style={{ fontWeight: 700 }}>{m.format('MM/DD/YYYY')}</span>
      </div>
    </div>
  )
}

const STATUS_COLORS = {
  approved: { bg: '#e8f5e9', color: '#2e7d32', label: 'approved' },
  pending: { bg: '#fff3e0', color: '#ef6c00', label: 'pending' },
  rejected: { bg: '#ffebee', color: '#c62828', label: 'rejected' },
  superseded: { bg: '#eceff1', color: '#546e7a', label: 'superseded' }
}

function formatTargetWindow(record) {
  if (!record?.targetStart || !record?.targetEnd) return ''
  const start = moment(record.targetStart)
  const end = moment(record.targetEnd)
  return `${start.format('h:mm A')} - ${end.format('h:mm A')}`
}

function DayCell({ scanCount, statusCounts, onClick, align = 'center' }) {
  if (scanCount === 0) {
    return null
  }

  const activeStatuses = Object.entries(statusCounts || {}).filter(
    ([status, count]) => count > 0 && status !== 'superseded'
  )

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: align === 'center' ? 'center' : 'flex-start',
        gap: 6,
        cursor: 'pointer'
      }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px 6px',
          justifyContent: align === 'center' ? 'center' : 'flex-start'
        }}>
        {activeStatuses.map(([status, count]) => {
          const style = STATUS_COLORS[status] || STATUS_COLORS.pending
          return (
            <span
              key={status}
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: style.color,
                whiteSpace: 'nowrap',
                background: style.bg,
                borderRadius: 4,
                padding: '2px 8px'
              }}>
              {count} {style.label}
            </span>
          )
        })}
      </div>
    </div>
  )
}

// ─── Main component ──────────────────────────────────────────────────
const AttendanceListing = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const { url } = useRouteMatch()
  const qrCodeRef = useRef(null)
  const [confFilter, setConfFilter] = useState('')

  const [showQrCodeDialog, setShowQrCodeDialog] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeDayFilter, setActiveDayFilter] = useState(null)
  const [drawerAttendeeId, setDrawerAttendeeId] = useState(null)
  const [drawerDayFilter, setDrawerDayFilter] = useState(null)
  const [decidingRecordId, setDecidingRecordId] = useState(null)
  const [rejectingRecordId, setRejectingRecordId] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [reApproveRecordId, setReApproveRecordId] = useState(null)

  const { user } = useSelector((state) => state.auth)
  const {
    conferences,
    inProgress: confProgress,
    conference
  } = useSelector((state) => state.conference)
  const { inProgress, attendeesAttendance, currentPage, pageSize } = useSelector(
    (state) => state.attendance
  )

  const orderedConferences = useMemo(() => {
    const filtered = conferences?.filter(
      (row) => row.isActive === true && !row.isArchived && row?.enableAttendance
    )
    return caseInsensitiveSort(filtered, 'confName') || []
  }, [conferences])

  const selectedConference = useMemo(() => {
    return orderedConferences.find((conference) => conference?.id === confFilter)
  }, [orderedConferences, confFilter])

  const getConfDatesBasedOnRole = useCallback(() => {
    let startDate = ''
    let endDate = ''
    if (user?.role === Roles.SUPER_ADMIN && selectedConference?.id) {
      startDate = moment(selectedConference?.startDate)
        .tz(selectedConference?.timezone)
        ?.format('MM/DD/YYYY')
      endDate = moment(selectedConference?.endDate)
        .tz(selectedConference?.timezone)
        ?.format('MM/DD/YYYY')
    } else if (user?.role !== Roles.SUPER_ADMIN && conference?.id) {
      startDate = moment(conference?.startDate).tz(conference?.timezone)?.format('MM/DD/YYYY')
      endDate = moment(conference?.endDate).tz(conference?.timezone)?.format('MM/DD/YYYY')
    }
    return getDatesInRange(startDate, endDate)
  }, [user, selectedConference, conference])

  const conferenceDates = useMemo(getConfDatesBasedOnRole, [getConfDatesBasedOnRole])

  useEffect(() => {
    if (user?.role !== Roles.SUPER_ADMIN && user?.conferenceId) {
      dispatch(fetchAttendeesAttendance(user?.conferenceId))
    }

    return () => {
      dispatch(attendanceSlice.actions.setAttendeesAttendance([]))
    }
  }, [user?.conferenceId])

  useEffect(() => {
    if (
      user?.role === Roles.SUPER_ADMIN &&
      !confFilter &&
      !confProgress &&
      orderedConferences.length > 0
    ) {
      const firstConfId = orderedConferences[0]?.id
      setConfFilter(firstConfId)
      dispatch(fetchAttendeesAttendance(firstConfId))
    }
  }, [user?.role, confFilter, confProgress, orderedConferences])

  const handleDecideRecord = async (attendanceId, decision, reason = '') => {
    const activeConfId = user?.role === Roles.SUPER_ADMIN ? confFilter : conference?.id
    setDecidingRecordId(attendanceId)
    try {
      await decideAttendanceRecord(attendanceId, decision, user?.id, reason)
      toast.success(`Attendance ${decision}.`)
      if (activeConfId) {
        await dispatch(fetchAttendeesAttendance(activeConfId))
      }
    } catch (err) {
      console.error('Failed to decide attendance', err)
      toast.error('Could not update this attendance record.')
    } finally {
      setDecidingRecordId(null)
    }
  }

  const handleStartReject = (recordId) => {
    setRejectingRecordId(recordId)
    setRejectReason('')
  }

  const handleCancelReject = () => {
    setRejectingRecordId(null)
    setRejectReason('')
  }

  const handleConfirmReject = async (recordId) => {
    await handleDecideRecord(recordId, 'rejected', rejectReason.trim())
    setRejectingRecordId(null)
    setRejectReason('')
  }

  const handleConfirmReApprove = async () => {
    if (!reApproveRecordId) return
    await handleDecideRecord(reApproveRecordId, 'approved')
    setReApproveRecordId(null)
  }

  const handleDownloadCSV = () => {
    const confName =
      user?.role === Roles.SUPER_ADMIN ? selectedConference?.confName : conference?.confName
    downloadAttendanceReportInCSV(attendeesAttendance, confName)
  }

  const handleCloseQrCodeDialog = () => {
    setShowQrCodeDialog(false)
  }

  const handleDownloadQrCode = () => {
    toPng(qrCodeRef.current)
      .then(function (dataUrl) {
        const link = document.createElement('a')
        link.href = dataUrl
        link.download = `${
          user?.role === Roles.SUPER_ADMIN && confFilter
            ? selectedConference.confName
            : conference?.confName
        }_attendance_qr_code.png`
        link.click()
      })
      .catch(function (error) {
        console.error('Error generating QR code:', error)
      })
  }

  const filteredAttendees = useMemo(() => {
    let list = attendeesAttendance
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      list = list.filter(
        (a) => a.firstName?.toLowerCase().includes(q) || a.lastName?.toLowerCase().includes(q)
      )
    }
    if (activeDayFilter) {
      list = list.filter(
        (a) => (a.attendance.find((x) => x.date === activeDayFilter)?.scanCount ?? 0) > 0
      )
    }
    return list
  }, [attendeesAttendance, searchQuery, activeDayFilter])

  const pagedAttendees = useMemo(
    () => filteredAttendees.slice(currentPage * pageSize, (currentPage + 1) * pageSize),
    [filteredAttendees, currentPage, pageSize]
  )

  const drawerAttendee = useMemo(
    () => attendeesAttendance.find((a) => a.id === drawerAttendeeId) || null,
    [attendeesAttendance, drawerAttendeeId]
  )

  return (
    <Box>
      <Box className={classes.pageHeader}>
        <Typography variant="h5" className={classes.headerTitle}>
          Attendance
        </Typography>
        <Box style={{ display: 'flex', gap: '12px' }}>
          <Button
            size="medium"
            className={classes.pendingButton}
            startIcon={<FactCheck />}
            variant="contained"
            disabled={user?.role === Roles.SUPER_ADMIN && !confFilter}
            onClick={() => {
              const activeConfId = user?.role === Roles.SUPER_ADMIN ? confFilter : conference?.id
              history.push(`${url}/pending${activeConfId ? `?conferenceId=${activeConfId}` : ''}`)
            }}>
            Pending & Rejected Attendance
          </Button>
          <Button
            size="medium"
            className={classes.qrCodeButton}
            startIcon={<QrCode />}
            variant="contained"
            disabled={user?.role === Roles.SUPER_ADMIN && !confFilter}
            onClick={() => setShowQrCodeDialog(true)}>
            Generate QR Code
          </Button>
        </Box>
      </Box>
      {user?.role === Roles.SUPER_ADMIN && (confProgress || orderedConferences.length > 0) && (
        <Box className={classes.controlsContainer}>
          <Box className={classes.filterDropdown}>
            <Typography className={classes.filterLabel}>Conference: </Typography>
            {confProgress ? (
              <Box className={classes.loadingRow}>
                <CircularProgress size={18} />
                <Typography className={classes.loadingText}>Loading, please wait...</Typography>
              </Box>
            ) : (
              <FormControl variant="outlined" size="small" className={classes.select}>
                <Select
                  value={confFilter}
                  onChange={(event) => {
                    const selectedConfId = event.target.value
                    setConfFilter(selectedConfId)
                    setActiveDayFilter(null)
                    setSearchQuery('')
                    if (selectedConfId) {
                      dispatch(fetchAttendeesAttendance(selectedConfId))
                    }
                  }}>
                  {orderedConferences.map((conf) => (
                    <MenuItem value={conf?.id} key={conf?.id}>
                      {conf?.confName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          <Button
            size="medium"
            className={classes.button}
            startIcon={<Download />}
            variant="contained"
            color="primary"
            onClick={handleDownloadCSV}
            disabled={!attendeesAttendance.length}>
            Download Attendance Report
          </Button>
        </Box>
      )}

      <Wrapper inProgress={inProgress}>
        {attendeesAttendance?.length ? (
          <Paper variant="outlined" className={classes.tableContainer}>
            {/* Filter bar */}
            <Box className={classes.filterBar}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search by attendee name..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  dispatch(attendanceSlice.actions.setCurrentPage(0))
                }}
                style={{ width: 320 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: '#90a4ae', fontSize: 18 }} />
                    </InputAdornment>
                  )
                }}
              />
              <Chip
                label="All days"
                onClick={() => {
                  setActiveDayFilter(null)
                  dispatch(attendanceSlice.actions.setCurrentPage(0))
                }}
                color={activeDayFilter === null ? 'primary' : 'default'}
                style={{ fontWeight: activeDayFilter === null ? 700 : 400 }}
                clickable
              />
              {conferenceDates.map((date) => (
                <Chip
                  key={date}
                  label={moment(date, 'MM/DD/YYYY').format('MM/DD ddd')}
                  onClick={() => {
                    setActiveDayFilter(activeDayFilter === date ? null : date)
                    dispatch(attendanceSlice.actions.setCurrentPage(0))
                  }}
                  color={activeDayFilter === date ? 'primary' : 'default'}
                  clickable
                />
              ))}
            </Box>

            {filteredAttendees.length === 0 ? (
              <Box
                style={{
                  padding: '40px 24px',
                  textAlign: 'center',
                  color: '#90a4ae',
                  fontSize: 14
                }}>
                {activeDayFilter
                  ? `No attendance recorded on ${moment(activeDayFilter, 'MM/DD/YYYY').format(
                      'MMM D, YYYY'
                    )}.`
                  : 'No attendees match your search.'}
              </Box>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table
                  style={{
                    borderCollapse: 'collapse',
                    width: '100%',
                    minWidth: 800,
                    tableLayout: 'fixed'
                  }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f6fa' }}>
                      <th style={{ ...stickyHeaderCell, width: 220 }}>Full Name</th>
                      {conferenceDates
                        .filter((date) => !activeDayFilter || date === activeDayFilter)
                        .map((date) => (
                          <th key={date} style={activeDayFilter ? stickyHeaderCell : headerCell}>
                            <DayColumnHeader date={date} />
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedAttendees.map((attendee, rowIdx) => {
                      const rowBg = rowIdx % 2 === 0 ? '#fff' : '#fafafa'
                      return (
                        <tr key={attendee.id} style={{ backgroundColor: rowBg }}>
                          <td
                            style={{ ...stickyCell, backgroundColor: rowBg, maxWidth: 140 }}
                            onClick={() => {
                              setDrawerAttendeeId(attendee.id)
                              setDrawerDayFilter(null)
                            }}>
                            <div style={{ cursor: 'pointer' }}>
                              <span style={{ color: '#1565c0', fontWeight: 500 }}>
                                {attendee?.lastName}, {attendee?.firstName}
                              </span>
                              {attendee?.email && (
                                <div style={{ fontSize: 12, color: '#78909c' }}>
                                  {attendee.email}
                                </div>
                              )}
                            </div>
                          </td>
                          {attendee.attendance
                            .filter(({ date }) => !activeDayFilter || date === activeDayFilter)
                            .map(({ date, scanCount, statusCounts }) => (
                              <td
                                key={date}
                                style={
                                  activeDayFilter
                                    ? { ...stickyCell, backgroundColor: rowBg }
                                    : bodyCellCenter
                                }>
                                <DayCell
                                  scanCount={scanCount}
                                  statusCounts={statusCounts}
                                  align={activeDayFilter ? 'left' : 'center'}
                                  onClick={() => {
                                    setDrawerAttendeeId(attendee.id)
                                    setDrawerDayFilter(date)
                                  }}
                                />
                              </td>
                            ))}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <TablePagination
              component="div"
              count={filteredAttendees.length}
              page={currentPage}
              rowsPerPage={pageSize}
              onPageChange={(_, page) => dispatch(attendanceSlice.actions.setCurrentPage(page))}
              onRowsPerPageChange={(e) => {
                dispatch(attendanceSlice.actions.setPageSize(parseInt(e.target.value, 10)))
                dispatch(attendanceSlice.actions.setCurrentPage(0))
              }}
            />
          </Paper>
        ) : (
          !inProgress && (
            <ResourceNotFound
              message={
                user?.role === Roles.SUPER_ADMIN && !confFilter
                  ? 'Please select a conference to view attendance data'
                  : user?.role === Roles.SUPER_ADMIN && orderedConferences.length === 0
                  ? 'No conferences found with the attendance module enabled. Please enable the attendance module to view attendance.'
                  : 'No attendance data available for this conference'
              }
            />
          )
        )}
      </Wrapper>

      {/* QR Code Dialog */}
      <Dialog
        open={showQrCodeDialog}
        onClose={handleCloseQrCodeDialog}
        fullWidth
        maxWidth="md"
        classes={{ paper: classes.dialogPaper }}>
        <Box className={classes.dialogTitle}>
          <Typography variant="h5">Attendance QR Code</Typography>
          <IconButton onClick={handleCloseQrCodeDialog} className={classes.iconBtn}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Box className={classes.dialogContent}>
          <Box className={classes.qrHeader}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Download />}
              className={classes.downloadBtn}
              color="primary"
              onClick={handleDownloadQrCode}>
              Download QR Code
            </Button>
          </Box>
          <Box ref={qrCodeRef} className={classes.qrCodeContainer}>
            <QRCode
              value={buildAttendanceQrValue(
                user?.role === Roles.SUPER_ADMIN && confFilter
                  ? selectedConference.confCode
                  : conference?.confCode
              )}
              size={480}
            />
          </Box>
        </Box>
      </Dialog>

      {/* Attendee Detail Drawer */}
      <Drawer
        anchor="right"
        open={Boolean(drawerAttendee)}
        onClose={() => {
          setDrawerAttendeeId(null)
          setDrawerDayFilter(null)
          setRejectingRecordId(null)
          setRejectReason('')
        }}>
        <Box style={{ width: 600, padding: '1.5rem' }}>
          <Box
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              borderBottom: '1px solid rgba(0,0,0,0.08)',
              paddingBottom: '1rem',
              marginBottom: '1rem'
            }}>
            <Box>
              <Typography variant="h6" style={{ fontWeight: 700 }}>
                {drawerAttendee?.lastName}, {drawerAttendee?.firstName}
              </Typography>
            </Box>
            <IconButton
              onClick={() => {
                setDrawerAttendeeId(null)
                setDrawerDayFilter(null)
                setRejectingRecordId(null)
                setRejectReason('')
              }}
              className={classes.iconBtn}>
              <Close />
            </IconButton>
          </Box>

          {drawerAttendee?.attendance?.every(
            ({ records }) => records.filter((r) => r.status !== 'superseded').length === 0
          ) && (
            <Typography
              style={{
                textAlign: 'center',
                color: '#90a4ae',
                fontSize: 14,
                padding: '48px 0'
              }}>
              No attendance records found for this attendee.
            </Typography>
          )}

          {drawerAttendee?.attendance
            ?.filter(({ date }) => !drawerDayFilter || date === drawerDayFilter)
            .map(({ date, records }) => ({
              date,
              records: records.filter((r) => r.status !== 'superseded')
            }))
            .filter(({ records }) => records.length > 0)
            .slice()
            .reverse()
            .map(({ date, records }) => {
              const latestFirstRecords = records.slice().reverse()
              const dateMoment = moment(date, 'MM/DD/YYYY')
              const dateLabel = dateMoment.format('MMM D, YYYY')
              return (
                <Box
                  key={date}
                  style={{
                    marginBottom: 12,
                    padding: '10px 0'
                  }}>
                  <Box style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
                    <Typography
                      variant="caption"
                      style={{
                        fontWeight: 400,
                        fontSize: 12,
                        color: '#78909c',
                        background: 'rgba(0,0,0,0.04)',
                        borderRadius: 12,
                        padding: '3px 12px'
                      }}>
                      {dateLabel}
                    </Typography>
                  </Box>
                  {latestFirstRecords.length > 0 && (
                    <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {latestFirstRecords.map((record) => {
                        const style = STATUS_COLORS[record.status] || STATUS_COLORS.pending
                        return (
                          <Box
                            key={record.id}
                            style={{
                              padding: '8px 10px',
                              borderRadius: 6,
                              background: '#fff',
                              border: '1px solid rgba(0,0,0,0.06)',
                              boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
                            }}>
                            <Box
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                gap: 8
                              }}>
                              <Box>
                                <Typography style={{ fontSize: 13 }}>
                                  {record.targetTitle}
                                </Typography>
                                {formatTargetWindow(record) && (
                                  <Typography
                                    variant="caption"
                                    style={{
                                      color: '#546e7a',
                                      display: 'block'
                                    }}>
                                    Session time: {formatTargetWindow(record)}
                                    <span style={{ marginLeft: 16 }}>
                                      Scanned At: {record.scanTimeLabel}
                                    </span>
                                  </Typography>
                                )}
                              </Box>
                              {record.status !== 'superseded' && (
                                <span
                                  style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: style.color,
                                    background: style.bg,
                                    borderRadius: 10,
                                    padding: '3px 8px',
                                    whiteSpace: 'nowrap',
                                    textTransform: 'uppercase'
                                  }}>
                                  {record.status}
                                </span>
                              )}
                            </Box>
                            {record.status === 'superseded' && (
                              <Typography
                                variant="caption"
                                style={{ color: '#ef6c00', display: 'block', marginTop: 4 }}>
                                Superseded by attendance
                                {record.supersedingTargetTitle
                                  ? ` at ${record.supersedingTargetTitle}`
                                  : ''}
                              </Typography>
                            )}

                            {record.decisionHistory?.length > 0 && (
                              <Box style={{ marginTop: 4 }}>
                                {record.decisionHistory.map((h, idx) => {
                                  const decidedByLabel =
                                    h.decidedBy === user?.id ? 'You' : h.decidedByName || 'an admin'
                                  if (h.decision === 'rejected') {
                                    return (
                                      <Box key={idx} style={{ display: 'block' }}>
                                        {h.reason && (
                                          <Typography
                                            variant="caption"
                                            style={{ color: '#c62828', display: 'block' }}>
                                            {h.reason}
                                          </Typography>
                                        )}
                                        <Typography
                                          variant="caption"
                                          style={{ color: '#78909c', display: 'block' }}>
                                          Rejected by: {decidedByLabel}
                                        </Typography>
                                      </Box>
                                    )
                                  }
                                  return (
                                    <Typography
                                      key={idx}
                                      variant="caption"
                                      style={{ color: '#78909c', display: 'block' }}>
                                      Approved
                                      {h.decidedBy !== user?.id
                                        ? ` by ${h.decidedByName || 'an admin'}`
                                        : ''}
                                      {h.decidedAt?.toDate
                                        ? ` on ${moment(h.decidedAt.toDate()).format(
                                            'MMM D, h:mm A'
                                          )}`
                                        : ''}
                                    </Typography>
                                  )
                                })}
                              </Box>
                            )}

                            {record.status === 'pending' && (
                              <Typography
                                variant="caption"
                                style={{ color: '#78909c', display: 'block', marginTop: 4 }}>
                                Attendee marked attendance after the grace time
                              </Typography>
                            )}

                            {record.status === 'pending' && rejectingRecordId === record.id && (
                              <Box style={{ marginTop: 8 }}>
                                <TextField
                                  size="small"
                                  variant="outlined"
                                  fullWidth
                                  multiline
                                  minRows={2}
                                  placeholder="Optional reason for rejecting (max 200 characters)"
                                  value={rejectReason}
                                  onChange={(e) => setRejectReason(e.target.value.slice(0, 200))}
                                  helperText={`${rejectReason.length}/200`}
                                  FormHelperTextProps={{
                                    style: { textAlign: 'right', margin: 0, fontSize: 11 }
                                  }}
                                  InputProps={{ style: { fontSize: 13 } }}
                                />
                                <Box
                                  style={{
                                    display: 'flex',
                                    gap: 8,
                                    justifyContent: 'flex-end',
                                    marginTop: 4
                                  }}>
                                  <Button
                                    size="small"
                                    disabled={Boolean(decidingRecordId)}
                                    onClick={handleCancelReject}>
                                    Cancel
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="secondary"
                                    style={{ color: '#fff' }}
                                    disabled={Boolean(decidingRecordId)}
                                    onClick={() => handleConfirmReject(record.id)}>
                                    Confirm Reject
                                  </Button>
                                </Box>
                              </Box>
                            )}

                            {record.status === 'pending' && rejectingRecordId !== record.id && (
                              <Box
                                style={{
                                  display: 'flex',
                                  gap: 8,
                                  marginTop: 8
                                }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  disabled={Boolean(decidingRecordId)}
                                  onClick={() => handleDecideRecord(record.id, 'approved')}>
                                  Approve
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  disabled={Boolean(decidingRecordId)}
                                  onClick={() => handleStartReject(record.id)}>
                                  Reject
                                </Button>
                              </Box>
                            )}

                            {record.status === 'rejected' && (
                              <Box style={{ marginTop: 8 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  disabled={Boolean(decidingRecordId)}
                                  onClick={() => setReApproveRecordId(record.id)}>
                                  Approve
                                </Button>
                              </Box>
                            )}
                          </Box>
                        )
                      })}
                    </Box>
                  )}
                </Box>
              )
            })}
        </Box>
      </Drawer>

      <ConfirmationModal
        open={Boolean(reApproveRecordId)}
        closeModal={() => setReApproveRecordId(null)}
        dialogTitle="Approve Attendance"
        dialogText="This record was rejected earlier. Approve it now?"
        inProgress={decidingRecordId === reApproveRecordId}
        handleConfirm={handleConfirmReApprove}
      />
    </Box>
  )
}

export default AttendanceListing

const useStyles = makeStyles({
  pageHeader: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    padding: '0 4px 16px'
  },
  headerTitle: {
    fontWeight: 600,
    color: '#263238'
  },
  controlsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    padding: '16px 20px',
    marginBottom: '20px',
    borderRadius: '8px',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    backgroundColor: '#fafafa'
  },
  button: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '15px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.08)',
    padding: '8px 16px',
    whiteSpace: 'nowrap'
  },
  select: {
    width: '280px',
    backgroundColor: '#fff',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px'
    }
  },
  filterDropdown: {
    display: 'flex',
    alignItems: 'center'
  },
  filterLabel: {
    fontSize: '1rem',
    marginRight: '12px',
    fontWeight: 'bold',
    color: '#37474f',
    whiteSpace: 'nowrap'
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  loadingText: {
    fontSize: 14,
    color: '#546e7a'
  },
  iconBtn: {
    padding: '4px'
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 1rem 1rem 1.5rem',
    borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
  },
  dialogPaper: {
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)'
  },
  qrCodeButton: {
    color: '#fff',
    backgroundColor: '#f57c00',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '15px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    padding: '8px 16px',
    '&:hover': {
      backgroundColor: '#ef6c00',
      boxShadow: '0 4px 8px rgba(0,0,0,0.12)'
    }
  },
  pendingButton: {
    color: '#fff',
    backgroundColor: '#1565c0',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '15px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    padding: '8px 16px',
    '&:hover': {
      backgroundColor: '#0d47a1',
      boxShadow: '0 4px 8px rgba(0,0,0,0.12)'
    }
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '1.5rem 1rem 2rem 1rem'
  },
  qrCodeContainer: {
    backgroundColor: '#fff',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '560px',
    height: '560px',
    padding: '1.5rem',
    borderRadius: '8px',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.06)',
    marginTop: '1rem'
  },
  downloadBtn: {
    textTransform: 'none',
    fontWeight: 500,
    letterSpacing: '0.4px',
    fontSize: '15px',
    borderRadius: '4px',
    padding: '6px 16px'
  },
  qrHeader: {
    width: '480px',
    display: 'flex',
    justifyContent: 'end'
  },
  tableContainer: {
    marginTop: 20,
    boxShadow: '0 3px 10px rgba(0, 0, 0, 0.08)',
    borderRadius: '8px',
    overflow: 'hidden',
    border: 'none'
  },
  filterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    padding: '12px 16px',
    alignItems: 'center',
    borderBottom: '1px solid rgba(0,0,0,0.06)'
  },
  scanTimelineRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 0',
    borderBottom: '1px solid rgba(0,0,0,0.06)'
  }
})
